import React, { useState } from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import { DistrictSelectInput, Modal } from '../../components';
import { District } from '../../app/feathers/districts/District';
import { IconButton, Skeleton } from '@mui/material';
import { Cancel, CheckCircle } from '@mui/icons-material';

interface DistrictCellProps {
  district?: District | null;
  suggestCreationAs?: {
    number?: number;
    name?: string;
    letter?: string;
    type?: string;
    parentDistrict?: string;
  };
  onDistrictChange?: (newDistrict: District) => void;
  state: string;
  loading?: boolean;
  confidence?: number;
  onChangeConfidence?: (confidence: number) => void;
  limitTypes?: string[];
  disableEditing?: boolean;
}

const DistrictCell: React.FC<DistrictCellProps> = ({ 
  district, 
  suggestCreationAs, 
  onDistrictChange, 
  state, 
  loading, 
  confidence, 
  onChangeConfidence,
  limitTypes,
  disableEditing
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDistrictChange = (selectedOption: any) => {
    if (selectedOption && selectedOption.target?.value && onDistrictChange) {
      onDistrictChange(selectedOption.target.value);
    }
    setIsEditing(false);
  };

  const handleClick = () => {
    // If there's no district but there's a suggestCreationAs value, open modal
    if (!district && suggestCreationAs) {
      setIsModalOpen(true);
      return;
    }
    
    setIsEditing(true);
  };

  const handleCreateDistrict = (newDistrict: District) => {
    setIsModalOpen(false);
    if (onDistrictChange) {
      onDistrictChange(newDistrict);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (loading) {
    return <Skeleton variant="text" width={100} height={24} />;
  }

  if(disableEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" style={{ fontWeight: '500' }}>-</Typography>
      </div>
    );
  }

  if (isEditing || (!district && !suggestCreationAs)) {
    return (
      <EditingContainer>
        <DistrictSelectInputAny
          descendantOf={state}
          value={null}
          onChange={handleDistrictChange}
          districtTypes={limitTypes}
          style={{ 
            minWidth: '120px',
          }}
        />
      </EditingContainer>
    );
  }

  // Show yellow tile for suggested district creation
  if (!district && suggestCreationAs) {
    const suggestionText = 
      `${suggestCreationAs.type || 'District'} ${suggestCreationAs.number || suggestCreationAs.letter || '' || suggestCreationAs.name} `.trim();
    
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>  
          <SuggestedDistrictContainer onClick={handleClick}>
            <Typography variant="body2" style={{ color: '#8B4513' }}>
              Create <span style={{ fontWeight: '700', color: '#8B4513' }}>{suggestionText}</span>
            </Typography>
          </SuggestedDistrictContainer>
          <IconButton onClick={() => {
            setIsEditing(true);
          }} style={{ marginLeft: '4px' }} size="small">
            <Cancel fontSize="small" />
          </IconButton>
        </div>
        {isModalOpen && (
          <Modal onClose={handleCloseModal}>
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Typography variant="h6">Create District Modal</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
                This is a placeholder for the Create District modal.
              </Typography>
            </div>
          </Modal>
        )}
      </>
    );
  }

  const displayName = district?.longName || '-';

  // Show checkmark for low confidence labels when district is provided
  const showConfidenceCheckmark = district && confidence !== undefined && confidence < 0.5;

  const handleConfidenceConfirm = () => {
    if (onChangeConfidence) {
      onChangeConfidence(1.0);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <DistrictContainer 
        onClick={handleClick}
        $isLowConfidence={showConfidenceCheckmark || false}
      >
        <Typography variant="body2" style={{ fontWeight: '500' }}>
          {displayName}
        </Typography>
      </DistrictContainer>
      {showConfidenceCheckmark && (
        <IconButton 
          onClick={handleConfidenceConfirm} 
          style={{ marginLeft: '4px' }} 
          size="small"
          title="Confirm this district (set confidence to 100%)"
        >
          <CheckCircle fontSize="small" style={{ color: '#1976d2' }} />
        </IconButton>
      )}
    </div>
  );
};

const DistrictSelectInputAny = ({ ...props }: any) => {
  return <DistrictSelectInput {...props} />;
};

interface DistrictContainerProps {
  $isLowConfidence?: boolean;
}

const DistrictContainer = styled.div<DistrictContainerProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: ${props => props.$isLowConfidence ? '#fff3e0' : '#e8f5e8'};
  border: ${props => props.$isLowConfidence ? '1px solid #ffb74d' : 'none'};
  border-radius: 12px;
  max-width: 200px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.$isLowConfidence ? '#ffe0b2' : '#c8e6c9'};
  }
`;

const SuggestedDistrictContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 12px;
  max-width: 200px;
  cursor: pointer;
  
  &:hover {
    background-color: #ffeaa7;
  }
`;

const EditingContainer = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 200px;
`;

export default DistrictCell; 